import { useId, useLayoutEffect, useRef, useState, type Ref } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { PublicMapResident, PublicResident } from "@/shared/contracts";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { Icon } from "@/shared/ui";
import { ResidentPortrait } from "../ResidentPortrait";
import { agoText, nowLine, ORIGINAL_CHARACTER_TAG } from "../residentView";
import { identityFaceCrop, type DailyStory } from "./guestStoriesModel";
import { isLastKnown, placeText } from "./publicMapModel";

/**
 * 点一只＝就近看 TA（UI-WORLD-LIVING-117-2，用户 10-15 经 I，UI-WORLD-AWARD-REVIEW-116 B 节）：在星球、城市层上点一位，就地弹出这块面板，不跳页。
 * 117-2 只给访客；117-2b 起登录后的 /world 也用它（I 10-16：体验要一致，原来登录后的短卡 MapResidentCard 删掉）。谁在看由页面给（viewer）：
 * 访客走访客读法、访客取图，「认识 TA」带 view=guest；登录后、已确认没登录各走自己的读法和缓存键（和居民主页同一个），不带 view=guest。
 * 层次照 r7k 参考稿 04-panel-B（I 10-16）：上面一张图、名字居中、一句此刻、一句 TA 的话、一个按钮。这一包只调结构和层次，换皮等正式素材。
 * - 上面那张图：有 TA 自己的真实生活照（故事里 TA 的那张，P114 的具名公开照片）就整幅放（16:9），图下说「生活片段」和多久前；
 *   点照片全屏看整张（模态，Esc、关上都能关，焦点回到照片；Esc 只关照片，不连卡一起关）；
 *   没有就放官方形象近景（审过头部框的放大），居中在名字上面。
 *   睡着（asleep＝true，B 的实际睡眠读数）时不拿以前的照片当此刻：形象调暗，「此刻」那一句先说「睡着了」；那句本来就说了睡（如「在家睡着了」）就不再加。
 *   asleep 为 null 是不知道，照常显示，不说睡没睡。不用 CSS 拼月亮之类的图形冒充插画（R1 高 3）；睡着一档的正式素材等出图恢复后另做。
 * - 一句人话说此刻（服务端 doing、place_name，和地图同一句），不另加「此刻」小标签；
 *   地图上那个点是「上次在这一带」时，下面多一行小字照实说（和短卡同一句，不把上次的位置说成此刻），别的时候不写。
 * - 一句 TA 的话：只用后台 bio 原话（公开主页同一个读口、同一个缓存键，进「认识 TA」不再重读），不改写、不编、不加「TA 说」，不装成引语；
 *   最多露两行，全文在主页（读屏照读全文）。没有 bio、或 bio 和性格一样时说性格那一句；主页还在读时先占两行空位，读到了不整卡往上跳（R1 中 3）。
 * - 主按钮只有一个「认识 TA」；从一处几位的名单进来的，多一个「回到名单」。
 * - 卡本身不滚：关上钮、按钮一直在原处（R1 高 2）。
 * 非模态的小对话框（role="dialog"，名字只读名字），开关、位置和焦点由 PublicWorldMap 管；访客页开着它时底边的上拉面板让开。
 * 放在哪由地图按 nearPlacement 算（R2 高 1：别盖住被点的那一位）；挤的时候先轻移地图，把被点的那一位往上挪、给下面腾出整张（I 10-16 07:20 裁定，见 nearPlacement）；
 * 还放不下才收：地图给卡挂 data-fit，wide 是生活照收成一条矮的宽幅（3:1，不矮于 64px，点了照样全屏看），
 * tight 再把形象近景、「上次在这一带」那行和 TA 的话收起来，只留名字、此刻和「认识 TA」（生活照照样是矮的宽幅）。生活照挤也不缩成小圆（I 10-16）。
 * 有生活照的还有最后一档 bare：照片也不放，只留名字、此刻和按钮。地图挪到头、最挤那档还会盖住被点的那一位时才用（I 10-16 07:20：照片不整张拿掉；
 * 第 5 条：被点的那一位不能被盖；评审 R6 高 1）。平面地图挪得到顶栏，竖屏三个尺寸都用不到它；横着拿的矮屏、3D 球转到头还不够时（小屏访客页、
 * 光点很靠球边）才会落到这一档（117-2b 评审 R3 高 1：只转纬度时偏心的光点常落到这里，现在经度一起转正，见 world/globeCamera）。
 * 「认识 TA」点下去时告诉地图（onLeave）：从主页回来时把这张卡重新打开，停在原处（I 10-16）。
 */
export function NearPanel({ viewer, mapResident, resident, story, nowMs, cardRef, onClose, onBack, onLeave }: {
  viewer: NearViewer;
  mapResident: PublicMapResident;
  /** 名单里同一位（性格、睡没睡）；名单还没读到时为 null */
  resident: PublicResident | null;
  /** 故事排序里 TA 最靠前的那一条（./guestStoriesModel 的 storyOf）；没有为 null */
  story: DailyStory | null;
  nowMs: number;
  cardRef: Ref<HTMLElement>;
  onClose: () => void;
  onBack?: () => void;
  /** 点「认识 TA」进主页时（地图记下是哪一位，回来时重新打开这张卡） */
  onLeave?: () => void;
}) {
  const nameId = useId();
  const { pets } = useServices();
  const petId = mapResident.pet_id;
  const guest = viewer.kind === "guest";
  // TA 的话和居民主页同一个读口、同一个缓存键（ResidentPage 的 PublicPetPage）：访客 publicPetGuest；登录后 publicPetMember；已确认没登录 publicPet＋session-anonymous；
  // 身份还没确认（pending）不读，先说性格那一句
  const readable = guest ? typeof pets.publicPetGuest === "function" : viewer.kind !== "pending";
  const view = useQuery({
    queryKey: viewer.kind === "guest" ? queryKeys.publicPetGuest(petId)
      : viewer.kind === "member" ? queryKeys.publicPetMember(viewer.userId, petId)
        : [...queryKeys.publicPet(petId), viewer.kind === "anonymous" ? "session-anonymous" : "identity-pending"],
    queryFn: () => guest ? pets.publicPetGuest!(petId) : pets.publicPet(petId),
    enabled: readable,
    staleTime: 30_000,
    ...(guest ? { retry: false as const, refetchOnWindowFocus: false as const, refetchOnReconnect: false as const } : {}),
  });
  const personality = resident?.personality?.trim() || null;
  const bio = view.isSuccess ? view.data.profile.bio?.trim() || null : null;
  const waiting = readable && view.isPending;
  // 在读时这一行先占位（下面 JSX 按 waiting 分支），这里不再另判
  const words = bio && bio !== personality ? bio : personality;
  const asleep = (mapResident.asleep ?? resident?.asleep) === true;
  const now = nowLine(mapResident);
  // TA 自己的那张生活照（照片的主人是 TA 才算；合照里别人的照片不借来当 TA 的图）；睡着时不拿以前的照片当此刻
  const ownPhoto = !asleep && story?.photo && story.photo.petId === petId ? story.photo : null;
  const [badPhoto, setBadPhoto] = useState<string | null>(null);
  const photo = ownPhoto && badPhoto !== ownPhoto.url ? ownPhoto : null;
  const ago = photo && story?.at ? agoText(story.at, nowMs) : "";
  const detail = `/world/residents/${encodeURIComponent(petId)}${guest ? "?view=guest" : ""}`;
  // 取图照地图头像（MapResidentCard 的 MapPortraitForMode）：访客走访客那条路（版本对不上就不取），登录后照常取
  const guestMedia = guest ? mapResident.avatar_version === mapResident.identity?.version ? { petId, identity: mapResident.identity } : null : undefined;
  // 点照片全屏看整张（I 10-16 07:20：挤成宽幅时点了全屏看；整张时也一样，一种点法）：模态弹层，Esc、关上都能关，焦点回到照片
  const [viewing, setViewing] = useState(false);
  const viewerRef = useRef<HTMLDialogElement>(null);
  const openRef = useRef<HTMLButtonElement>(null);
  const showing = Boolean(photo && viewing);
  useLayoutEffect(() => {
    const dialog = viewerRef.current;
    if (!showing || !dialog) return;
    const trigger = openRef.current;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    return () => {
      const heldFocus = dialog.contains(document.activeElement) || document.activeElement === document.body;
      if (dialog.open) dialog.close();
      document.body.style.overflow = overflow;
      // 照着故事面板的照片弹层（GuestWorldStories）：只在焦点丢了、或还在弹层里时还给照片，不抢别处的焦点
      if (heldFocus) queueMicrotask(() => {
        const active = document.activeElement;
        if (active !== document.body && active !== trigger && !dialog.contains(active)) return;
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      });
    };
  }, [showing]);
  const closeViewer = () => setViewing(false);
  const portrait = (size: number, focus: boolean) => <ResidentPortrait name={mapResident.name} species={mapResident.species} photoUrl={mapResident.avatar_url || mapResident.identity?.url || null}
    fallbackUrl={mapResident.identity?.fallback_url ?? null} official guestMedia={guestMedia} size={size} surface="paper"
    focus={focus ? identityFaceCrop({ pet_id: petId, identity: mapResident.identity, avatar_url: mapResident.avatar_url, official_avatar: true }) : null} />;
  return (
    <section ref={cardRef} className={`ps-pmap-card ps-near${asleep ? " is-asleep" : ""}${photo ? " has-photo" : ""}`} role="dialog" aria-labelledby={nameId} tabIndex={-1} data-testid="map-card" data-motion="enter">
      <button type="button" className="ps-near__close" aria-label="关上小卡" onClick={onClose}>
        <Icon name="close" size={20} />
      </button>
      {photo ? <figure className="ps-near__media" data-media="photo">
        <img src={photo.url} alt={photo.alt} draggable={false} onError={() => { setViewing(false); setBadPhoto(photo.url); }} />
        <figcaption>生活片段{ago ? <> · <time dateTime={story!.at!}>{ago}</time></> : null}</figcaption>
        <button ref={openRef} type="button" className="ps-near__photo-open" aria-label={`放大查看${mapResident.name}的生活照片`} onClick={() => setViewing(true)} />
      </figure> : null}
      {/* Esc 只关照片：拦在这里，不让地图的 Esc（关整张卡）也收到 */}
      {showing && photo ? <dialog ref={viewerRef} className="ps-discovery-photo-preview ps-near__viewer" aria-label={`${mapResident.name}的完整生活照片`}
        onCancel={(event) => { event.preventDefault(); closeViewer(); }}
        onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); closeViewer(); } }}>
        <div className="ps-discovery-photo-preview__bar"><span>{mapResident.name}的生活片段</span><button type="button" autoFocus aria-label="关上照片" onClick={closeViewer}>×</button></div>
        <img src={photo.url} alt={photo.alt} draggable={false} onError={() => { closeViewer(); setBadPhoto(photo.url); }} />
        <p>{photo.alt}</p>
      </dialog> : null}
      {photo ? null
        : asleep ? <div className="ps-near__media ps-near__quiet" data-media="asleep">{portrait(80, false)}</div>
        : <div className="ps-near__media" data-media="identity">{portrait(96, true)}</div>}
      <div className="ps-near__head">
        <h2 className="ps-near__name"><span id={nameId}>{mapResident.name}</span>{mapResident.resident_kind === "public_npc" ? <small>{ORIGINAL_CHARACTER_TAG}</small> : null}</h2>
        <p className="ps-near__now">{asleep && !/睡/.test(now) ? <span className="ps-near__asleep">睡着了，</span> : null}{now}</p>
        {isLastKnown(mapResident) ? <p className="ps-near__where">{placeText(mapResident)}</p> : null}
      </div>
      {/* TA 的话：挤的时候这一段自己滚（键盘也能进来滚），给它一个名字 */}
      <div className="ps-near__words" role="group" aria-label={`${mapResident.name}的话`}>
        {waiting ? <p className="ps-near__says-wait" aria-hidden="true" /> : words ? <p className="ps-near__says">{words}</p> : null}
      </div>
      <div className="ps-near__actions">
        {onBack ? <button type="button" className="ps-near__back" onClick={onBack}><Icon name="back" size={16} /> 回到名单</button> : null}
        <Link className="ps-near__more" to={detail} aria-label={`认识 TA：${mapResident.name}`} onClick={onLeave}>认识 TA</Link>
      </div>
    </section>
  );
}

/** 就近面板挤成哪一档：整张；生活照收成一条矮的宽幅；再把形象近景、「上次在这一带」和 TA 的话收起来（只留名字、此刻和「认识 TA」）；
 *  有生活照的最后一档连照片也不放 */
export type NearFit = "full" | "wide" | "tight" | "bare";

/** 沿用原来那一档时的余差（px），见 nearPlacement */
const PREFER_SLACK = 2;

/**
 * 就近面板放在哪（评审 R1–R4 高 1：别盖住被点的那一位，也别把按钮挤没）。都是地图区域里的 px：
 * top 是卡能上到的地方（顶栏下沿），bottom 是卡照旧贴底时的底边（量卡本身的布局位置，不按底边面板推算、也不受进场动画影响），
 * pinTop、pinBottom 是被点的那一处（已留了边），sizes 是从整张到最挤的几档各多高（地图挂上那一档再量出来的真高，R4：减法估不准）。
 * 一档一档试：先试照旧贴底（盖不到那一处就行），再试放在那一处上面（卡底挨着它）；哪一档放得下就用哪一档。
 * 能挪地图时（canPan，117-2b，I 10-16 07:20 裁定「挤的那几档先轻移地图」）每一档多试一种：把那一处往上挪（最多挪到顶栏下沿，也不超过地图说的 reach），卡照旧贴底；
 * reach 是地图那边最多能把那一处往上挪多少（3D 球转不了那么多；评审 R2 阻断：原来只按顶栏算，球转不到，卡就整张盖在那一位上），不给就不限；
 * 要挪多少放在 pan 里（px，那一处往上挪的量；不挪就没有这一项），由地图去挪镜头。所以整张贴底、整张在上面都放不下时，先挪地图放整张，挪到头也不够才收成宽幅。
 * 都放不下就用最挤那一档照旧贴底：盖得最少，按钮、字也不裁（R3 阻断；R6 高 1：原来回落成整张，320 下有生活照时把被点的那一位整个盖住）。
 * prefer 是这张卡上一次放的地方（字变了、挪完地图再放一次时给）：那一档在原来那一边还放得下、现在也放不下更大的一档时就不挪，
 * 免得小传读到以后整张卡从点的上面跳到下面（R4 中 1）；现在放得下更大的一档就换大的（117-2b 评审 R1 中 2：从主页回来重开那一下版面还没稳，
 * 先量出小一档，之后地方够了也一直停在小的那一档）。
 * 沿用原来那一档时让 PREFER_SLACK（2px）的余差：挪完地图、转完球再放一次，实机量出来常差零点几像素（评审 R3 实测 0.1–0.4px，取整一类的误差），
 * 不让它因此掉一档、把照片收掉（117-2b 评审 R3 高 1、中 2）；原来留的边（下 18px、上 44px）够让这 2px。
 */
export function nearPlacement({ top, bottom, pinTop, pinBottom, sizes, prefer = null, canPan = false, reach = Number.POSITIVE_INFINITY }: {
  top: number; bottom: number; pinTop: number; pinBottom: number; sizes: readonly (readonly [NearFit, number])[];
  prefer?: { fit: NearFit; above: boolean } | null;
  canPan?: boolean;
  reach?: number;
}): { top: number | null; fit: NearFit; pan?: number } {
  const below = bottom - pinBottom, above = pinTop - top;
  const room = canPan ? Math.max(0, Math.min(above, reach)) : 0;
  const fresh = ((): { top: number | null; fit: NearFit; pan?: number } => {
    for (const [fit, height] of sizes) {
      if (height <= below) return { top: null, fit };
      if (height <= above) return { top: pinTop - height, fit };
      if (height <= below + room) return { top: null, fit, pan: Math.ceil(height - below) };
    }
    return { top: null, fit: sizes.length ? sizes[sizes.length - 1][0] : "full" };
  })();
  const rank = (fit: NearFit) => sizes.findIndex(([f]) => f === fit);
  const kept = prefer ? sizes.find(([fit]) => fit === prefer.fit)?.[1] : undefined;
  if (prefer && kept !== undefined && kept <= (prefer.above ? above : below) + PREFER_SLACK && rank(fresh.fit) >= rank(prefer.fit)) return { top: prefer.above ? pinTop - kept : null, fit: prefer.fit };
  return fresh;
}

/** 谁在看就近面板（页面按会话算好给）：访客；登录后（已确认的成员编号）；已确认没登录；身份还没确认 */
export type NearViewer = { kind: "guest" } | { kind: "member"; userId: string } | { kind: "anonymous" } | { kind: "pending" };
